import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { FirstPage, LastPage } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useCueCommandMutation } from '@/state/mutations/player/use-cue-command';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { performTransportIconButtonSx } from '../preload-transition-shared';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';

import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowControlSegmentBrowse } from '@/components/flows/detail/slot-list/use-flow-control-segment-browse';
import { isSimulationScopeIdleShowing } from '@/helpers/simulation-cue-engine.helper';
import { resolveFlowControlSimulationFlow } from '@/helpers/simulation-flow.helper';
import { usePerformAdvanceCue } from '@/hooks/use-perform-advance-cue';
import { resolveLiveNextMediaStopCommand } from '@/helpers/perform-transport-show-info.helper';
import {
  resolveCueTransportCanStep,
  resolveCurrentCueSheetPointer,
  resolveLiveCueTransportCanStep,
} from '@/helpers/resolve-next-cue-preview-row.helper';
import { PerformEqualSpreadIconRow, performEqualSpreadIconButtonSx } from './perform-equal-spread-icon-row';

export interface PerformTransportBarProps {
  flow: FlowDto;
  entityRef: string;
  performMode?: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  liveContextQueryUpdatedAt?: number;
  /** Lower controls: equal-width touch targets across the half-row on mobile. */
  equalSpreadOnMobile?: boolean;
  /** Optional third control (e.g. settings) included in the equal-spread triple. */
  endAdornment?: ReactNode;
}

export function PerformTransportBar({
  flow,
  entityRef,
  performMode = 'play',
  liveContextState,
  liveContextQueryUpdatedAt,
  equalSpreadOnMobile = false,
  endAdornment,
}: PerformTransportBarProps) {
  const t = useTranslations();
  const cueCommand = useCueCommandMutation();
  const stepMedia = useFlowControlSimulationCueStore((s) => s.stepMedia);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationOverridePointer = useFlowControlSimulationCueStore((s) => s.overridePointer);
  const { isBrowsing, snapToPlayingSegment, followPlayingSegmentAfterSimulationStep } = useFlowControlSegmentBrowse();
  const { advanceCue, isPending, tryLatchBusy, markMutationFailed, markMutationSettled } = usePerformAdvanceCue({
    flow,
    entityRef,
    performMode,
    liveContextState,
    liveContextQueryUpdatedAt,
  });
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);

  const currentPointer = useMemo(
    () =>
      resolveCurrentCueSheetPointer(simulationRows, performMode, simulationPointer, cueState, {
        simulationOverridePointer,
      }),
    [simulationRows, performMode, simulationPointer, simulationOverridePointer, cueState],
  );
  const scopeIdleShowing =
    performMode === 'simulation' &&
    isSimulationScopeIdleShowing(simulationRows, simulationPointer, simulationSelectedItemIds);
  const hasOnAirMedia =
    performMode === 'simulation' &&
    !scopeIdleShowing &&
    simulationSelectedItemIds.some((id) => id.startsWith('media:'));
  /** At a cue end while browsing, prev/next only return the list. A real step takes on this click. */
  const canSnapBrowse = isBrowsing && !hasOnAirMedia;

  const { canPrev, canNext } = useMemo(() => {
    // Play once: prev/next end the insert and return to resume.
    if (performMode === 'simulation' && simulationOverridePointer != null) {
      return { canPrev: true, canNext: true };
    }
    if (performMode === 'play') {
      const live = resolveLiveCueTransportCanStep(cueState);
      if (resolveLiveNextMediaStopCommand(flow, entityRef, cueState)) {
        return { canPrev: live.canPrev, canNext: true };
      }
      return live;
    }
    return resolveCueTransportCanStep(simulationRows, currentPointer);
  }, [simulationRows, currentPointer, performMode, simulationOverridePointer, flow, entityRef, cueState]);

  const handlePrevMedia = () => {
    if (canSnapBrowse) {
      snapToPlayingSegment();
    }
    if (!canPrev) {
      return;
    }
    if (performMode === 'simulation') {
      stepMedia(-1, resolveFlowControlSimulationFlow(flow));
      followPlayingSegmentAfterSimulationStep();
      return;
    }
    if (!tryLatchBusy()) {
      return;
    }
    cueCommand.mutate(
      { action: 'prevMedia', entityRef },
      {
        onError: () => markMutationFailed(),
        onSettled: () => markMutationSettled(),
      },
    );
  };

  const buttonSx = equalSpreadOnMobile ? performEqualSpreadIconButtonSx : performTransportIconButtonSx;

  const prevButton = (
    <Tooltip key="prev" title={t('flows.detail.perform.transport.prevMedia', { defaultValue: 'Previous media' })}>
      <span style={equalSpreadOnMobile ? { display: 'flex', width: '100%', justifyContent: 'center' } : undefined}>
        <IconButton
          size="medium"
          color="secondary"
          disabled={isPending || (!canPrev && !canSnapBrowse)}
          onClick={handlePrevMedia}
          aria-label={t('flows.detail.perform.transport.prevMedia', { defaultValue: 'Previous media' })}
          sx={buttonSx}
        >
          <FirstPage />
        </IconButton>
      </span>
    </Tooltip>
  );

  const nextButton = (
    <Tooltip key="next" title={t('flows.detail.perform.transport.nextMedia', { defaultValue: 'Next media' })}>
      <span style={equalSpreadOnMobile ? { display: 'flex', width: '100%', justifyContent: 'center' } : undefined}>
        <IconButton
          size="medium"
          color="secondary"
          disabled={isPending || (!canNext && !canSnapBrowse)}
          onClick={() => {
            if (!canNext) {
              if (canSnapBrowse) {
                snapToPlayingSegment();
              }
              return;
            }
            advanceCue();
          }}
          aria-label={t('flows.detail.perform.transport.nextMedia', { defaultValue: 'Next media' })}
          sx={buttonSx}
        >
          <LastPage />
        </IconButton>
      </span>
    </Tooltip>
  );

  if (equalSpreadOnMobile) {
    return (
      <PerformEqualSpreadIconRow>
        {prevButton}
        {nextButton}
        {endAdornment}
      </PerformEqualSpreadIconRow>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        flexShrink: 0,
      }}
    >
      {prevButton}
      {nextButton}
      {endAdornment}
    </Box>
  );
}
